import Decimal from 'decimal.js';

/**
 * Pieniądze: w bazie numeric(12,2), w TS zawsze `string` ("250.00"), obliczenia decimal.js.
 * Zaokrąglenie do grosza połówkowo w górę (ROUND_HALF_UP). Nigdy `number` dla kwot.
 */
export type Money = string;

/** Osobny konstruktor, żeby nie zmieniać globalnej konfiguracji decimal.js. */
export const Dec = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
export type Dec = InstanceType<typeof Dec>;

/** Niełamliwa spacja: separator tysięcy i odstęp przed „zł” (np. "4 275 zł"). */
export const NBSP = ' ';

const SECONDS_PER_HOUR = 3600;

export function toDec(value: Decimal.Value | Dec): Dec {
  if (typeof value === 'string') {
    const trimmed = value.trim().replace(',', '.');
    if (trimmed === '' || !/^[-+]?\d*\.?\d+(e[-+]?\d+)?$/i.test(trimmed)) {
      throw new Error(`Nieprawidłowa kwota: "${value}"`);
    }
    return new Dec(trimmed);
  }
  return new Dec(value);
}

/** Zaokrągla do grosza (HALF_UP) i zwraca string z dwoma miejscami, np. "250.00". */
export function roundMoney(value: Decimal.Value | Dec): Money {
  return toDec(value).toDecimalPlaces(2, Dec.ROUND_HALF_UP).toFixed(2);
}

export function isValidMoney(value: unknown): value is Money {
  return typeof value === 'string' && /^-?\d{1,10}(\.\d{1,2})?$/.test(value);
}

/**
 * Kwota wpisu czasu = stawka × sekundy / 3600, zaokrąglona do grosza.
 * Przykład: computeAmount("150", 6000) === "250.00".
 */
export function computeAmount(rate: Money | Decimal.Value, seconds: number): Money {
  if (!Number.isInteger(seconds) || seconds < 0) throw new Error('Czas musi być nieujemną liczbą całkowitą sekund');
  return roundMoney(toDec(rate).times(seconds).dividedBy(SECONDS_PER_HOUR));
}

/** Kwota wpisu z uwzględnieniem rozliczalności: wpis niefakturowany ma kwotę "0.00". */
export function computeEntryAmount(input: {
  rate: Money | null | undefined;
  seconds: number;
  billable: boolean;
}): Money {
  if (!input.billable || input.rate == null) return '0.00';
  return computeAmount(input.rate, input.seconds);
}

export function sumMoney(values: Iterable<Money | null | undefined>): Money {
  let total = new Dec(0);
  for (const v of values) {
    if (v == null || v === '') continue;
    total = total.plus(toDec(v));
  }
  return roundMoney(total);
}

export function addMoney(a: Money, b: Money): Money {
  return roundMoney(toDec(a).plus(toDec(b)));
}

export function subtractMoney(a: Money, b: Money): Money {
  return roundMoney(toDec(a).minus(toDec(b)));
}

export function multiplyMoney(a: Money, factor: Decimal.Value): Money {
  return roundMoney(toDec(a).times(toDec(factor)));
}

export function compareMoney(a: Money, b: Money): -1 | 0 | 1 {
  return toDec(a).comparedTo(toDec(b)) as -1 | 0 | 1;
}

export function isZeroMoney(a: Money): boolean {
  return toDec(a).isZero();
}

function groupThousands(intPart: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

/**
 * Formatowanie PLN po polsku.
 * - `formatPLN("250")` → "250,00 zł" (zawsze dwa miejsca po przecinku),
 * - `formatPLN("4275", { decimals: false })` → "4 275 zł" (zaokrąglone do złotówki, np. kafelki i sumy).
 * Separator tysięcy i odstęp przed „zł” to niełamliwa spacja (NBSP).
 */
export function formatPLN(
  amount: Money | Decimal.Value,
  options: { decimals?: boolean; currency?: string } = {},
): string {
  const decimals = options.decimals ?? true;
  const d = toDec(amount);
  const fixed = decimals
    ? d.toDecimalPlaces(2, Dec.ROUND_HALF_UP).toFixed(2)
    : d.toDecimalPlaces(0, Dec.ROUND_HALF_UP).toFixed(0);
  const negative = fixed.startsWith('-') && !/^-0(\.0+)?$/.test(fixed);
  const unsigned = fixed.replace(/^-/, '');
  const [intPart = '0', frac] = unsigned.split('.');
  const body = groupThousands(intPart) + (frac ? `,${frac}` : '');
  const currency = options.currency && options.currency !== 'PLN' ? options.currency : 'zł';
  return `${negative ? '-' : ''}${body}${NBSP}${currency}`;
}

/** Skrót: kwota zaokrąglona do złotówki, np. "4 275 zł". */
export function formatPLNShort(amount: Money | Decimal.Value): string {
  return formatPLN(amount, { decimals: false });
}

// ── Waluty: kwot w różnych walutach nigdy nie sumujemy ──────────────────────

/** Waluta bazowa (MVP: PLN). Kwoty w innych walutach (EUR, USD) pokazujemy obok, bez przeliczania. */
export const BASE_CURRENCY = 'PLN';

export interface CurrencyAmount {
  currency: string;
  amount: Money;
}

/** Suma w walucie głównej + sumy w pozostałych walutach (np. kafelek „Niezafakturowane” z klientami PLN i EUR). */
export interface MoneyTotal {
  amount: Money;
  currency: string;
  /** kwoty w innych walutach niż `currency`; brak = jedna waluta */
  otherAmounts?: CurrencyAmount[];
}

/** Kolejność walut: PLN najpierw, potem alfabetycznie. */
export function compareCurrencies(a: string, b: string): number {
  if (a === b) return 0;
  if (a === BASE_CURRENCY) return -1;
  if (b === BASE_CURRENCY) return 1;
  return a < b ? -1 : 1;
}

/**
 * Sumy kwot per waluta. Waluty z sumą 0 są pomijane; gdy wszystkie są zerowe (albo lista jest pusta) — jedna
 * pozycja `0.00` w `fallback` (domyślnie PLN). Kolejność: PLN, potem alfabetycznie.
 */
export function sumByCurrency(
  items: Iterable<{ currency: string; amount?: Money | null }>,
  fallback: string = BASE_CURRENCY,
): CurrencyAmount[] {
  const sums = new Map<string, Dec>();
  for (const it of items) {
    if (it.amount == null || it.amount === '') continue;
    sums.set(it.currency, (sums.get(it.currency) ?? new Dec(0)).plus(toDec(it.amount)));
  }
  const out = [...sums.entries()]
    .filter(([, v]) => !v.isZero())
    .sort(([a], [b]) => compareCurrencies(a, b))
    .map(([currency, v]) => ({ currency, amount: roundMoney(v) }));
  return out.length > 0 ? out : [{ currency: fallback, amount: '0.00' }];
}

/** Lista z `sumByCurrency` jako kwota główna + pozostałe waluty (`otherAmounts` tylko przy kilku walutach). */
export function toMoneyTotal(list: readonly CurrencyAmount[], fallback: string = BASE_CURRENCY): MoneyTotal {
  const [first, ...rest] = list;
  const total: MoneyTotal = { amount: first?.amount ?? '0.00', currency: first?.currency ?? fallback };
  if (rest.length > 0) total.otherAmounts = rest.map((r) => ({ ...r }));
  return total;
}

/** Sumy per waluta jako `MoneyTotal` (skrót `toMoneyTotal(sumByCurrency(items))`). */
export function moneyTotalOf(
  items: Iterable<{ currency: string; amount?: Money | null }>,
  fallback: string = BASE_CURRENCY,
): MoneyTotal {
  return toMoneyTotal(sumByCurrency(items, fallback), fallback);
}

/**
 * Kilka walut obok siebie, bez przeliczania: „1 200 zł + 849,99 EUR”. `decimals: 'auto'` = bez groszy, gdy kwota
 * jest całkowita.
 */
export function formatAmounts(list: readonly CurrencyAmount[], options: { decimals?: boolean | 'auto' } = {}): string {
  const mode = options.decimals ?? true;
  return list
    .map((it) => {
      const decimals = mode === 'auto' ? !/^-?\d+(\.0+)?$/.test(it.amount.trim()) : mode;
      return formatPLN(it.amount, { decimals, currency: it.currency });
    })
    .join(' + ');
}

/** `MoneyTotal` jako tekst: kwota główna i pozostałe waluty („1 200 zł + 849,99 EUR”). */
export function formatMoneyTotal(total: MoneyTotal, options: { decimals?: boolean | 'auto' } = {}): string {
  return formatAmounts([{ amount: total.amount, currency: total.currency }, ...(total.otherAmounts ?? [])], options);
}

// ── Kwota pozycji wg stawki (DECISIONS 2026-10-05 [P]) ──────────────────────

/**
 * Czas wyceniany stawką: wpis czasu albo pozycja `time` zestawienia. `rate = null` (klient bez stawki) i wpis
 * nierozliczany (`billable = false`) nie tworzą kwoty.
 */
export interface RatedSeconds {
  rate: Money | null | undefined;
  seconds: number;
  /** domyślnie `BASE_CURRENCY` */
  currency?: string;
  /** domyślnie true */
  billable?: boolean;
}

/** Pozycja wg stawki: łączny czas grupy o tej samej stawce i walucie, kwota zaokrąglona raz do grosza. */
export interface RateLine {
  currency: string;
  rate: Money;
  seconds: number;
  amount: Money;
  /** liczba wpisów / pozycji w grupie */
  count: number;
}

/**
 * Reguła kwoty pozycji (DECISIONS 2026-10-05 [P]): kwota pozycji zestawienia i faktury = round(stawka × łączny czas
 * grupy o tej samej stawce i walucie, 2) — NIE suma kwot wpisów zaokrąglonych osobno. 3 × 1 h 40 min po 170 zł =
 * 170 × 5 h = 850,00 zł (a nie 3 × 283,33 = 849,99 zł). Ta sama reguła w sumach raportu czasu, więc suma raportu
 * = suma zestawienia, a wiersz „Stawka” w PDF jest zawsze prawdziwy. Kwota pojedynczego wpisu (`time_entries.amount`)
 * zostaje informacyjnie. Kolejność: waluta (PLN najpierw), potem stawka rosnąco.
 */
export function rateLines(items: Iterable<RatedSeconds>, defaultCurrency: string = BASE_CURRENCY): RateLine[] {
  const groups = new Map<string, { currency: string; rate: Dec; seconds: number; count: number }>();
  for (const it of items) {
    if (it.billable === false || it.rate == null || it.rate === '') continue;
    const rate = toDec(it.rate);
    const currency = it.currency ?? defaultCurrency;
    const key = `${currency}|${rate.toFixed(2)}`;
    const g = groups.get(key) ?? { currency, rate, seconds: 0, count: 0 };
    g.seconds += it.seconds;
    g.count += 1;
    groups.set(key, g);
  }
  return [...groups.values()]
    .sort((a, b) => compareCurrencies(a.currency, b.currency) || a.rate.comparedTo(b.rate))
    .map((g) => ({
      currency: g.currency,
      rate: g.rate.toFixed(2),
      seconds: g.seconds,
      amount: computeAmount(g.rate.toFixed(2), g.seconds),
      count: g.count,
    }));
}

/** Sumy wg reguły pozycji per waluta (jak `sumByCurrency`: waluty z sumą 0 pomijane, pusto = `0.00` w `fallback`). */
export function sumByRate(items: Iterable<RatedSeconds>, fallback: string = BASE_CURRENCY): CurrencyAmount[] {
  return sumByCurrency(rateLines(items, fallback), fallback);
}

/** Kwota wg reguły pozycji w jednej walucie (pozycje w innych walutach pominięte). */
export function amountByRate(items: Iterable<RatedSeconds>, currency: string = BASE_CURRENCY): Money {
  return sumMoney(
    rateLines(items, currency)
      .filter((l) => l.currency === currency)
      .map((l) => l.amount),
  );
}

/** `MoneyTotal` wg reguły pozycji (skrót `toMoneyTotal(sumByRate(items))`). */
export function moneyTotalByRate(items: Iterable<RatedSeconds>, fallback: string = BASE_CURRENCY): MoneyTotal {
  return toMoneyTotal(sumByRate(items, fallback), fallback);
}
